import { Box, keyframes, styled } from "@mui/material";
import { StoneType } from "../models";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { GameIcon } from "./IconSet";
import { DefaultStone } from "../components/DefaultStoneSets";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";

const PatternWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: "#e8e8e8",
  backgroundImage: `repeating-linear-gradient(
    45deg,
    rgba(200, 200, 200, 0.2),
    rgba(200, 200, 200, 0.2) 4px,
    rgba(150, 150, 150, 0.2) 4px,
    rgba(150, 150, 150, 0.2) 8px
  )`,
  borderRadius: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  padding: "8%",
}));

const BorderedStone = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: "white",
  border: "2px dashed rgba(100, 100, 100, 0.5)",
  borderRadius: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: "inset 0 1px 4px rgba(0,0,0,0.1)",
}));

const InnerCircle = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const StoneComponent = styled(Box)(({ theme }) => ({
  width: "96%",
  height: "96%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  // backgroundColor: "white",
  fontSize: "large",
  borderRadius: "50%",
}));

export function StoneControl(props: {
  stone?: StoneType;
  preview?: boolean;
  iconSet?: number | null;
  size?: "small" | "medium" | "large";
}) {
  // console.log("🚀 ~ SC props", props);
  const { stone, iconSet: stoneSet, size = "medium" } = props;
  const { themeName } = useAppTheme();

  const blink = keyframes`
  from { opacity: 0.1; }
  to { opacity: 1; }
`;

  const getScaleFactor = () => {
    switch (size) {
      case "small":
        return 0.6;
      case "medium":
        return 1.0;
      case "large":
        return 1.4;
      default:
        return 1.0;
    }
  };

  const scaleFactor = getScaleFactor();

  const getBorderWidth = () => {
    // Compensate for scale - smaller stones need relatively thicker borders
    switch (size) {
      case "small":
        return "2.5px";
      case "medium":
        return "2px";
      case "large":
        return "2px";
      default:
        return "2px";
    }
  };

  const getIconFontSize = () => {
    switch (size) {
      case "small":
        return "1.2rem";
      case "medium":
        return "2.0rem";
      case "large":
        return "3.0rem";
      default:
        return "2.0rem";
    }
  };

  const getStone = (stone?: StoneType) => {
    let icon;
    if (!stone) {
      return (
        <StoneComponent sx={{ transform: `scale(${scaleFactor})` }}>
          <MoreHorizIcon
            sx={{
              animation: `${blink} 1s linear infinite alternate`,
            }}
          />
        </StoneComponent>
      );
    }

    // Use Default Stones if no iconSet is specified (null or undefined)
    if (stoneSet === null || stoneSet === undefined) {
      return (
        <StoneComponent sx={{ transform: `scale(${scaleFactor})` }}>
          <DefaultStone stone={stone} themeSet={themeName} />
        </StoneComponent>
      );
    }

    // Use Icon-based stones if iconSet is specified
    if (stone.shape === 1) {
      icon = (
        <StoneComponent
          sx={{
            transform: `scale(${scaleFactor})`,
            backgroundColor: "white",
          }}
        >
          <GameIcon stoneSet={stoneSet || 1} {...stone} />
        </StoneComponent>
      );
    } else {
      icon = (
        <PatternWrapper
          sx={{
            transform: `scale(${scaleFactor})`,
            backgroundSize: size === "small" ? "6px 6px" : "10px 10px",
          }}
        >
          <BorderedStone
            sx={{
              borderWidth: getBorderWidth(),
            }}
          >
            <InnerCircle>
              <GameIcon stoneSet={stoneSet || 1} {...stone} />
            </InnerCircle>
          </BorderedStone>
        </PatternWrapper>
      );
    }
    return icon;
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignContent: "center",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
          color: props.preview ? "red" : "inherit",
        }}
      >
        {getStone(stone)}
      </Box>
    </>
  );
}
